'use client';
import { useDataAnalytics } from '@/hooks/analytics/analytics';
import { cn } from '@/lib/utils';
import { Slot } from '@radix-ui/react-slot';
import { cva, type VariantProps } from 'class-variance-authority';
import Link from 'next/link';
import * as React from 'react';

const anchorVariants = cva(
  // eslint-disable-next-line @stylistic/max-len
  "inline-flex items-center justify-center gap-[10px] whitespace-nowrap rounded-[30px] text-sm font-medium transition-all disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg:not([class*='size-'])]:size-4 shrink-0 [&_svg]:shrink-0 outline-none focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:ring-[3px] aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 aria-invalid:border-destructive font-bold",
  {
    defaultVariants: {
      size: 'default',
      variant: 'default',
    },
    variants: {
      size: {
        default: 'h-9 px-4 py-2 has-[>svg]:px-3',
        icon: 'size-9',
        lg: 'h-10 px-7 py-5 has-[>svg]:px-4 text-base',
        sm: 'h-8 gap-1.5 px-3 has-[>svg]:px-2.5',
      },
      variant: {
        default:
          'bg-primary text-(--on-primary) shadow-xs hover:bg-primary-darker',
        destructive:
          // eslint-disable-next-line @stylistic/max-len
          'bg-destructive text-white shadow-xs hover:bg-destructive/90 focus-visible:ring-destructive/20 dark:focus-visible:ring-destructive/40 dark:bg-destructive/60',
        ghost:
          'hover:bg-accent hover:text-accent-foreground dark:hover:bg-accent/50',
        link: 'text-primary font-bold underline-offset-4 hover:text-primary-darker',
        outline:
          // eslint-disable-next-line @stylistic/max-len
          'border-2 border-(--primary) text-primary bg-transparent shadow-xs hover:bg-accent hover:text-primary dark:bg-input/30 dark:border-input dark:hover:bg-input/50',
        secondary:
          'bg-(--on-secondary) text-primary border-(--primary) shadow-xs hover:bg-(--on-secondary)/80',
      },
    },
  }
);

const Anchor = ({
  asChild = false,
  className,
  dataLinkCategory,
  dataLinkName,
  href,
  size,
  variant,
  ...props
}: React.ComponentProps<'a'> &
  VariantProps<typeof anchorVariants> & {
    asChild?: boolean;
    dataLinkCategory?: string;
    dataLinkName?: string;
  }): React.JSX.Element => {
  const Comp = asChild ? Slot : Link;
  const { trackClick } = useDataAnalytics();

  const handleClick = (): void => {
    if (dataLinkCategory && dataLinkName) {
      trackClick({
        linkCategory: dataLinkCategory,
        linkName: dataLinkName,
      });
    }
  };

  return (
    <Comp className={cn(anchorVariants({ className, size, variant }), variant === 'link' ? 'p-0 h-6 text-base' : '')}
      data-slot="a"
      href={href!}
      onClick={handleClick}
      {...props}
    />
  );
};

export { Anchor, anchorVariants };
